import { Directive, ElementRef, HostListener, NgZone, OnDestroy, inject } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';

/** Delay before a hover-opened menu closes, giving the pointer time to travel between panels. */
const CLOSE_GRACE_MS = 50;

/**
 * Opens the host MatMenuTrigger on hover and closes it once the pointer leaves the trigger
 * and every open menu panel (including nested submenus). No-op on devices without a hover
 * pointer (touch), where the menu keeps its normal tap-to-open behaviour.
 */
@Directive({
  selector: '[appHoverMenuTrigger]',
  standalone: true,
})
export class HoverMenuTriggerDirective implements OnDestroy {
  private readonly trigger = inject(MatMenuTrigger, { self: true, optional: true });
  private readonly tooltip = inject(MatTooltip, { self: true, optional: true });
  private readonly host = inject(ElementRef<HTMLElement>).nativeElement;
  private readonly ngZone = inject(NgZone);

  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * The element the pointer last moved over. Tracked rather than read back from the `:hover`
   * pseudo-class so the close logic is driven by observable events: `:hover` is set by the
   * browser from real pointer position and can neither be asserted on nor simulated in a spec.
   */
  private lastPointerTarget: EventTarget | null = null;

  private readonly onPointerOver = (event: Event): void => {
    this.lastPointerTarget = event.target;
  };

  /**
   * Swallows a click on a trigger whose menu hover has already opened. Without this, the
   * trigger's own click handler toggles the menu shut, so a user who intuitively clicks what
   * hover just opened sees it vanish. The menu still closes the usual ways: the pointer leaving,
   * choosing an item, or a click outside.
   */
  private readonly onTriggerClick = (event: MouseEvent): void => {
    // detail === 0 marks a click synthesised by Enter or Space on the focused trigger. Toggling
    // is the expected keyboard behaviour there, and no pointer-out would ever close the menu.
    if (event.detail > 0 && this.hoverCapable() && this.trigger?.menuOpen) {
      event.stopImmediatePropagation();
    }
  };

  constructor() {
    // Hovering opens the menu immediately, so the trigger's tooltip is redundant — and it would
    // pop up in the gap below the trigger, interrupting the pointer's path into the panel.
    if (this.tooltip && this.hoverCapable()) {
      this.tooltip.disabled = true;
    }
    // Registered natively from the constructor rather than via @HostListener: Angular instantiates
    // every directive on a node before it registers any of their host listeners, so this lands
    // ahead of MatMenuTrigger's (click) and stopImmediatePropagation() can still reach it. Capture
    // phase covers clicks on the trigger's children; the registration order covers the trigger
    // itself, where capture and bubble listeners run in the order they were added.
    this.ngZone.runOutsideAngular(() => {
      this.host.addEventListener('click', this.onTriggerClick, true);
      document.addEventListener('pointerover', this.onPointerOver, true);
      document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
    });
  }

  @HostListener('mouseenter')
  protected onTriggerEnter(): void {
    if (!this.trigger || !this.hoverCapable()) {
      return;
    }
    this.disableBackdrop();
    this.cancelClose();
    if (!this.trigger.menuOpen) {
      // Open as a mouse interaction so Material doesn't keyboard-focus the first item (matches click).
      (this.trigger as unknown as { _openedBy?: string })._openedBy = 'mouse';
      this.trigger.openMenu();
    }
  }

  @HostListener('mouseleave')
  protected onTriggerLeave(): void {
    if (!this.trigger || !this.hoverCapable()) {
      return;
    }
    this.scheduleClose();
  }

  private hoverCapable(): boolean {
    return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(hover: hover)').matches;
  }

  // Without this, the top-level menu's full-screen backdrop sits over the trigger and fires
  // mouseleave the instant the menu opens, causing an open/close flicker loop.
  //
  // This does cost the trigger its outside-click close: MatMenuTrigger gets that from
  // `overlayRef.backdropClick()`, which never emits when no backdrop is attached. (Only
  // MatContextMenuTrigger uses `outsidePointerEvents()`.) onDocumentPointerDown replaces it.
  private disableBackdrop(): void {
    const menu = this.trigger?.menu as unknown as { hasBackdrop?: boolean } | null;
    if (menu && menu.hasBackdrop !== false) {
      menu.hasBackdrop = false;
    }
  }

  private scheduleClose(): void {
    this.cancelClose();
    this.ngZone.runOutsideAngular(() => {
      this.closeTimer = setTimeout(() => this.tryClose(), CLOSE_GRACE_MS);
    });
  }

  private tryClose(): void {
    // Stay open while the pointer is still on the trigger or on one of this trigger's own
    // panels (covers the gap between trigger and panel, and nested submenu panels).
    if (this.pointerWithinMenu()) {
      this.closeTimer = setTimeout(() => this.tryClose(), CLOSE_GRACE_MS);
      return;
    }
    this.ngZone.run(() => {
      if (this.trigger?.menuOpen) {
        this.trigger.closeMenu();
      }
    });
  }

  private pointerWithinMenu(): boolean {
    const target = this.lastPointerTarget;
    if (!(target instanceof Node)) {
      return false;
    }
    if (this.host.contains(target)) {
      return true;
    }
    return this.openPanels().some((panel) => panel.contains(target));
  }

  /**
   * This trigger's own open panels: its menu plus any submenu panels currently open beneath it.
   * Scoped deliberately — a document-wide panel lookup would also match a *different* hover
   * trigger's menu, and the pointer resting there would keep this one open indefinitely.
   *
   * Walks the rendered DOM using Material's public contract: a panel carries `id="<panelId>"`,
   * and an open submenu trigger carries `aria-controls="<panelId>"` pointing at its own panel.
   */
  private openPanels(): HTMLElement[] {
    const panels: HTMLElement[] = [];
    const seen = new Set<string>();
    const pending: string[] = [];
    const rootId = this.trigger?.menu?.panelId;
    if (rootId) {
      pending.push(rootId);
    }

    while (pending.length > 0) {
      const id = pending.shift() as string;
      if (seen.has(id)) {
        continue;
      }
      seen.add(id);
      const panel = document.getElementById(id);
      if (!panel) {
        continue;
      }
      panels.push(panel);
      panel.querySelectorAll<HTMLElement>('[aria-controls]').forEach((submenuTrigger) => {
        const childId = submenuTrigger.getAttribute('aria-controls');
        if (childId) {
          pending.push(childId);
        }
      });
    }
    return panels;
  }

  /**
   * Closes the menu when a press lands outside it. MatMenuTrigger routes its own outside-click
   * close through `overlayRef.backdropClick()`, so suppressing the backdrop (see disableBackdrop)
   * removes that path entirely — this replaces it.
   */
  private readonly onDocumentPointerDown = (event: Event): void => {
    if (!this.trigger?.menuOpen || !this.hoverCapable()) {
      return;
    }
    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }
    if (this.host.contains(target) || this.openPanels().some((panel) => panel.contains(target))) {
      return;
    }
    this.ngZone.run(() => {
      this.cancelClose();
      this.trigger?.closeMenu();
    });
  };

  private cancelClose(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.host.removeEventListener('click', this.onTriggerClick, true);
    document.removeEventListener('pointerover', this.onPointerOver, true);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
    this.cancelClose();
  }
}
